<template>
  <div class="card z-index-2">
    <div class="p-3 pb-0 card-header">
      <h6>{{ props.title }}</h6>
    </div>
    <div class="p-3 card-body">
      <div class="chart">
        <canvas :id="props.id" class="chart-canvas" :height="props.height"></canvas>
      </div>
    </div>
  </div>
</template>

<script setup>
import Chart from "chart.js/auto";
import {defineProps, onMounted, watch} from "vue"

const props = defineProps({
    id: {
      type: String,
      default: "chart-line",
    },
    title: {
      type: String,
      default: "Default Line Chart",
    },
    height: {
      type: String,
      default: "300",
    },
    chart: {
      type: Object,
      required: true,
      labels: Array,
      datasets: {
        type: Array,
        label: String,
        data: Array,
      },
    },
    pointradius: {
      type: Number,
      default: 2,
    }
})

watch(()=>props.chart, ()=>{
    if(props.chart){
        genChart();
    }
})

onMounted(()=>{
    if(props.chart && props.chart.datasets){
      genChart();
    }
})

async function genChart() {
    var chart = document.getElementById(props.id).getContext("2d");
    var gradientStroke2 = chart.createLinearGradient(0, 230, 0, 50);

    gradientStroke2.addColorStop(1, "rgba(20,23,39,0.2)");
    gradientStroke2.addColorStop(0.2, "rgba(72,72,176,0.0)");
    gradientStroke2.addColorStop(0, "rgba(20,23,39,0)");

    let chartStatus = Chart.getChart(props.id);
    if(chartStatus !== undefined) {
      chartStatus.destroy();
    }
    if(props.chart.datasets.length === 3) {
      new Chart(chart, {
        type: "line",
        data: {
          labels: props.chart.labels,
          datasets: [
            {
              label: props.chart.datasets[0].label,
              tension: 0.4,
              borderWidth: 0,
              pointRadius: props.pointradius,
              pointBackgroundColor: "#cb0c9f",
              borderColor: "#cb0c9f",
              // eslint-disable-next-line no-dupe-keys
              borderWidth: 3,
              backgroundColor: gradientStroke2,
              data: props.chart.datasets[0].data,
              maxBarThickness: 6,
            },
            {
              label: props.chart.datasets[1].label,
              tension: 0.4,
              borderWidth: 0,
              pointRadius: props.pointradius,
              pointBackgroundColor: "#3A416F",
              borderColor: "#3A416F",
              // eslint-disable-next-line no-dupe-keys
              borderWidth: 3,
              backgroundColor: gradientStroke2,
              data: props.chart.datasets[1].data,
              maxBarThickness: 6,
            },
            {
              label: props.chart.datasets[2].label,
              tension: 0.4,
              borderWidth: 0,
              pointRadius: props.pointradius,
              pointBackgroundColor: "#17c1e8",
              borderColor: "#17c1e8",
              // eslint-disable-next-line no-dupe-keys
              borderWidth: 3,
              backgroundColor: gradientStroke2,
              data: props.chart.datasets[2].data,
              maxBarThickness: 6,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
          },
          interaction: {
            intersect: false,
            mode: "index",
          },
          scales: {
            y: {
              grid: {
                drawBorder: false,
                display: true,
                drawOnChartArea: true,
                drawTicks: false,
                borderDash: [5, 5],
              },
              ticks: {
                display: true,
                padding: 10,
                color: "#9ca2b7",
              },
            },
            x: {
              grid: {
                drawBorder: false,
                display: true,
                drawOnChartArea: true,
                drawTicks: true,
                borderDash: [5, 5],
              },
              ticks: {
                display: true,
                color: "#9ca2b7",
                padding: 10,
              },
            },
          },
        },
      });
    } else if (props.chart.datasets.length === 2) {
      new Chart(chart, {
        type: "line",
        data: {
          labels: props.chart.labels,
          datasets: [
            {
              label: props.chart.datasets[0].label,
              tension: 0.4,
              borderWidth: 0,
              pointRadius: props.pointradius,
              pointBackgroundColor: "#cb0c9f",
              borderColor: "#cb0c9f",
              // eslint-disable-next-line no-dupe-keys
              borderWidth: 3,
              backgroundColor: gradientStroke2,
              data: props.chart.datasets[0].data,
              maxBarThickness: 6,
            },
            {
              label: props.chart.datasets[1].label,
              tension: 0.4,
              borderWidth: 0,
              pointRadius: props.pointradius,
              pointBackgroundColor: "#3A416F",
              borderColor: "#3A416F",
              // eslint-disable-next-line no-dupe-keys
              borderWidth: 3,
              backgroundColor: gradientStroke2,
              data: props.chart.datasets[1].data,
              maxBarThickness: 6,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
          },
          interaction: {
            intersect: false,
            mode: "index",
          },
          scales: {
            y: {
              grid: {
                drawBorder: false,
                display: true,
                drawOnChartArea: true,
                drawTicks: false,
                borderDash: [5, 5],
              },
              ticks: {
                display: true,
                padding: 10,
                color: "#9ca2b7",
              },
            },
            x: {
              grid: {
                drawBorder: false,
                display: true,
                drawOnChartArea: true,
                drawTicks: true,
                borderDash: [5, 5],
              },
              ticks: {
                display: true,
                color: "#9ca2b7",
                padding: 10,
              },
            },
          },
        },
      });
    } else if (props.chart.datasets.length === 1) {
      new Chart(chart, {
        type: "line",
        data: {
          labels: props.chart.labels,
          datasets: [
            {
              label: props.chart.datasets[0].label,
              tension: 0.4,
              borderWidth: 0,
              pointRadius: props.pointradius,
              pointBackgroundColor: "#4BB543",
              borderColor: "#4BB543",
              // eslint-disable-next-line no-dupe-keys
              borderWidth: 3,
              backgroundColor: gradientStroke2,
              data: props.chart.datasets[0].data,
              maxBarThickness: 6,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
          },
          interaction: {
            intersect: false,
            mode: "index",
          },
          scales: {
            y: {
              grid: {
                drawBorder: false,
                display: true,
                drawOnChartArea: true,
                drawTicks: false,
                borderDash: [5, 5],
              },
              ticks: {
                display: true,
                padding: 10,
                color: "#9ca2b7",
              },
            },
            x: {
              grid: {
                drawBorder: false,
                display: true,
                drawOnChartArea: true,
                drawTicks: true,
                borderDash: [5, 5],
              },
              ticks: {
                display: true,
                color: "#9ca2b7",
                padding: 10,
              },
            },
          },
        },
      });
    }
}
</script>
